import type { FormFieldAnswer } from "../../types/submit";
import { readLabeledChoiceValue } from "./fieldOptions";
import type { FormSubmissionDto } from "../../types/submission";
import { normalizeStaffMarks } from "./staffColumns";

function normalizeAges(raw: unknown): Record<string, number> | undefined {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return undefined;
  const ages: Record<string, number> = {};
  for (const [key, value] of Object.entries(raw as Record<string, unknown>)) {
    const numeric = Number(value);
    if (!key || !Number.isFinite(numeric)) continue;
    ages[key] = Math.trunc(numeric);
  }
  return ages;
}

function toFieldId(raw: unknown): number | null {
  if (raw == null || raw === "") return null;
  const parsed = Number(raw);
  return Number.isFinite(parsed) ? Math.trunc(parsed) : null;
}

export function normalizeSubmissionFieldId(raw: unknown): number | null {
  return toFieldId(raw);
}

type SubmissionFieldRef = {
  id?: number | null;
  name?: string;
  label?: string;
  order?: number;
};

function orderedSubmissionFields(
  fields: SubmissionFieldRef[],
): SubmissionFieldRef[] {
  return [...fields]
    .filter((field) => toFieldId(field.id) != null)
    .sort((left, right) => (left.order ?? 0) - (right.order ?? 0));
}

/** Яндекс иногда отдаёт `data` как `[value0, value1, …]` по порядку полей формы. */
function isPositionalSubmissionValues(values: unknown[]): boolean {
  return values.every(
    (item) => item == null || typeof item !== "object" || Array.isArray(item),
  );
}

function buildPositionalAnswersMap(
  values: unknown[],
  fields: SubmissionFieldRef[],
): Map<number, FormFieldAnswer["value"]> {
  const map = new Map<number, FormFieldAnswer["value"]>();
  const orderedFields = orderedSubmissionFields(fields);

  values.forEach((value, index) => {
    const fieldId = toFieldId(orderedFields[index]?.id);
    if (fieldId == null) return;
    map.set(fieldId, extractSubmissionValue(value));
  });

  return map;
}

function parseSubmissionDataRaw(raw: unknown): unknown {
  if (typeof raw === "string") {
    const trimmed = raw.trim();
    if (!trimmed) return raw;
    try {
      return JSON.parse(trimmed) as unknown;
    } catch {
      return raw;
    }
  }
  return raw;
}

function buildFieldIndexes(fields: SubmissionFieldRef[]) {
  const nameToId = new Map<string, number>();
  const labelToId = new Map<string, number>();
  const idSet = new Set<number>();

  for (const field of fields) {
    const fieldId = toFieldId(field.id);
    if (fieldId == null) continue;
    idSet.add(fieldId);
    nameToId.set(String(fieldId), fieldId);
    if (field.name) {
      nameToId.set(field.name, fieldId);
    }
    if (field.label) {
      labelToId.set(field.label, fieldId);
    }
  }

  return { nameToId, labelToId, idSet };
}

/** Стабильный отрицательный id для slug-ключа без поля в схеме формы. */
export function submissionSlugOrphanId(key: string): number {
  let hash = 0;
  for (let i = 0; i < key.length; i += 1) {
    hash = (hash * 31 + key.charCodeAt(i)) | 0;
  }
  return -Math.abs(hash || 1);
}

/** Разворачивает value из ответов Яндекса и других nested-форматов. */
export function extractSubmissionValue(raw: unknown): FormFieldAnswer["value"] {
  if (raw == null) return "";

  const labeled = readLabeledChoiceValue(raw);
  if (labeled) return labeled;

  if (Array.isArray(raw)) {
    const labels = raw
      .map((item) => {
        if (item == null) return "";
        if (typeof item === "object") {
          const record = item as Record<string, unknown>;
          return String(
            record.text ?? record.label ?? record.slug ?? record.key ?? "",
          ).trim();
        }
        return String(item).trim();
      })
      .filter(Boolean);

    if (labels.length === 0) return "";
    return labels.length === 1 ? labels[0]! : labels.join(", ");
  }

  if (typeof raw === "object") {
    const record = raw as Record<string, unknown>;
    if ("value" in record) {
      return extractSubmissionValue(record.value);
    }
    if ("text" in record) {
      return extractSubmissionValue(record.text);
    }
  }

  return raw as FormFieldAnswer["value"];
}

function resolveFieldIdFromRecord(
  record: Record<string, unknown>,
  nameToId: Map<string, number>,
  labelToId: Map<string, number>,
  idSet: Set<number>,
): number | null {
  const fieldId = toFieldId(record.field_id ?? record.fieldId);
  if (fieldId != null) return fieldId;

  const slug =
    typeof record.slug === "string"
      ? record.slug
      : typeof record.name === "string"
        ? record.name
        : typeof record.id === "string" && toFieldId(record.id) == null
          ? record.id
          : "";
  if (slug) {
    if (nameToId.has(slug)) return nameToId.get(slug)!;
    if (labelToId.has(slug)) return labelToId.get(slug)!;
  }

  const questionId = toFieldId(record.id);
  if (questionId != null && idSet.has(questionId)) {
    return questionId;
  }

  return null;
}

function resolveFieldIdFromObjectKey(
  key: string,
  nameToId: Map<string, number>,
  labelToId: Map<string, number>,
  idSet: Set<number>,
): number | null {
  if (nameToId.has(key)) return nameToId.get(key)!;
  if (labelToId.has(key)) return labelToId.get(key)!;

  const numericKey = toFieldId(key);
  if (numericKey != null && idSet.has(numericKey)) {
    return numericKey;
  }

  return null;
}

export function normalizeSubmissionAnswer(
  raw: unknown,
): FormFieldAnswer | null {
  if (!raw || typeof raw !== "object") return null;

  const item = raw as Record<string, unknown>;
  const fieldId = toFieldId(item.field_id ?? item.fieldId);
  if (fieldId == null) return null;

  return {
    field_id: fieldId,
    value: extractSubmissionValue(item.value ?? item),
  };
}

/** Ключи slug-объекта data (формат ответов Яндекса). */
export function collectSubmissionObjectKeys(raw: unknown): string[] {
  const parsed = parseSubmissionDataRaw(raw);
  if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) return [];
  return Object.keys(parsed as Record<string, unknown>);
}

/** Нормализует массив `{ field_id, value }` или slug-объект `{ slug: value }`. */
export function normalizeSubmissionData(
  raw: unknown,
  fields: SubmissionFieldRef[] = [],
): FormFieldAnswer[] {
  const map = buildSubmissionAnswersMap(raw, fields);
  return [...map.entries()]
    .sort(([leftId], [rightId]) => leftId - rightId)
    .map(([field_id, value]) => ({ field_id, value }));
}

/**
 * Собирает значения ответа по id поля.
 * Поддерживает `[value0, value1]` (Яндекс), `[{ field_id, value }]`, `{ slug: value }`.
 */
export function buildSubmissionAnswersMap(
  raw: unknown,
  fields: SubmissionFieldRef[] = [],
): Map<number, FormFieldAnswer["value"]> {
  const map = new Map<number, FormFieldAnswer["value"]>();
  const parsed = parseSubmissionDataRaw(raw);
  const { nameToId, labelToId, idSet } = buildFieldIndexes(fields);

  if (Array.isArray(parsed)) {
    if (isPositionalSubmissionValues(parsed)) {
      return buildPositionalAnswersMap(parsed, fields);
    }

    for (const item of parsed) {
      const answer = normalizeSubmissionAnswer(item);
      if (answer) {
        map.set(answer.field_id, answer.value);
        continue;
      }

      if (!item || typeof item !== "object") continue;
      const record = item as Record<string, unknown>;
      const fieldId = resolveFieldIdFromRecord(
        record,
        nameToId,
        labelToId,
        idSet,
      );

      if (fieldId != null) {
        map.set(fieldId, extractSubmissionValue(record));
      }
    }

    return map;
  }

  if (parsed && typeof parsed === "object") {
    for (const [key, rawValue] of Object.entries(
      parsed as Record<string, unknown>,
    )) {
      const value = extractSubmissionValue(rawValue);
      const fieldId = resolveFieldIdFromObjectKey(
        key,
        nameToId,
        labelToId,
        idSet,
      );

      if (fieldId != null) {
        map.set(fieldId, value);
        continue;
      }

      map.set(submissionSlugOrphanId(key), value);
    }
  }

  return map;
}

export function getSubmissionFieldValue(
  raw: unknown,
  field: SubmissionFieldRef,
  fields: SubmissionFieldRef[] = [],
): FormFieldAnswer["value"] | undefined {
  const fieldId = toFieldId(field.id);
  const map = buildSubmissionAnswersMap(raw, fields);
  if (fieldId != null && map.has(fieldId)) {
    return map.get(fieldId);
  }
  if (field.name) {
    const orphanId = submissionSlugOrphanId(field.name);
    if (map.has(orphanId)) return map.get(orphanId);
  }
  return undefined;
}

export function collectSubmissionFieldIds(raw: unknown): number[] {
  const ids = new Set<number>();
  const parsed = parseSubmissionDataRaw(raw);
  if (Array.isArray(parsed)) {
    for (const item of parsed) {
      const answer = normalizeSubmissionAnswer(item);
      if (answer) {
        ids.add(answer.field_id);
        continue;
      }

      if (!item || typeof item !== "object") continue;
      const record = item as Record<string, unknown>;
      const fieldId = toFieldId(record.field_id ?? record.fieldId ?? record.id);
      if (fieldId != null) ids.add(fieldId);
    }
    return [...ids];
  }

  if (parsed && typeof parsed === "object" && !Array.isArray(parsed)) {
    for (const key of Object.keys(parsed as Record<string, unknown>)) {
      const fieldId = toFieldId(key);
      if (fieldId != null) ids.add(fieldId);
    }
  }

  return [...ids];
}

export function normalizeSubmission(raw: unknown): FormSubmissionDto | null {
  if (!raw || typeof raw !== "object") return null;

  const item = raw as Record<string, unknown>;
  const id = toFieldId(item.id);
  const uuid =
    typeof item.uuid === "string" && item.uuid.trim()
      ? item.uuid.trim()
      : undefined;
  if (id == null) return null;

  return {
    id,
    uuid,
    form_id: toFieldId(item.form_id) ?? undefined,
    formId: toFieldId(item.formId) ?? undefined,
    data: item.data ?? [],
    ages: normalizeAges(item.ages),
    marks: normalizeStaffMarks(item.marks),
    created_at:
      typeof item.created_at === "string" ? item.created_at : undefined,
    createdAt: typeof item.createdAt === "string" ? item.createdAt : undefined,
    updated_at:
      typeof item.updated_at === "string" ? item.updated_at : undefined,
    updatedAt: typeof item.updatedAt === "string" ? item.updatedAt : undefined,
  };
}

export function submissionFromSubmitResponse(
  response: unknown,
  fallbackData: FormFieldAnswer[],
): FormSubmissionDto {
  if (response && typeof response === "object") {
    const record = response as Record<string, unknown>;
    const nested = record.submission;
    if (nested && typeof nested === "object" && !Array.isArray(nested)) {
      const item = nested as Record<string, unknown>;
      const normalized = normalizeSubmission({
        ...item,
        id: item.id ?? 0,
      });
      if (normalized) return normalized;
    }
  }

  return { id: 0, data: fallbackData };
}

export function normalizeSubmissionsList(raw: unknown): FormSubmissionDto[] {
  if (Array.isArray(raw)) {
    return raw
      .map(normalizeSubmission)
      .filter((item): item is FormSubmissionDto => item != null);
  }

  if (raw && typeof raw === "object") {
    const data = (raw as { data?: unknown }).data;
    return normalizeSubmissionsList(data);
  }

  return [];
}

/** @deprecated Используйте buildSubmissionAnswersMap — поддерживает legacy-формат data. */
export function buildAnswersByFieldId(
  data: FormFieldAnswer[] | undefined,
): Map<number, FormFieldAnswer["value"]> {
  return buildSubmissionAnswersMap(data);
}
